import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Loader2, User, Phone, Mail, MapPin, ShoppingBag, DollarSign, Calendar, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchAllOrders();
  }, [searchQuery]);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      const res = await axios.get(`${API}/customers?${params}`);
      setCustomers(res.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders?limit=1000`);
      setOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const openCustomerDetails = (customer) => {
    setSelectedCustomer(customer);
    // Filter orders for this customer
    const filteredOrders = orders.filter(
      o => o.customer_phone === customer.phone || 
           o.customer_email === customer.email
    );
    setCustomerOrders(filteredOrders);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-orange-500/20 text-orange-500',
      confirmed: 'bg-blue-500/20 text-blue-500',
      shipped: 'bg-purple-500/20 text-purple-500',
      delivered: 'bg-green-500/20 text-green-500',
      cancelled: 'bg-red-500/20 text-red-500'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  // Calculate stats
  const totalCustomers = customers.length;
  const totalRevenue = customers.reduce((sum, c) => sum + (c.total_spent || 0), 0);
  const avgOrderValue = totalCustomers > 0 
    ? customers.reduce((sum, c) => sum + (c.total_spent || 0), 0) / customers.reduce((sum, c) => sum + (c.total_orders || 0), 0) || 0
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div>
        <h1 className="text-2xl font-serif text-white">Clientes</h1>
        <p className="text-white/60 text-sm">{customers.length} clientes registrados</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Clientes</p>
              <p className="text-2xl font-semibold text-white mt-1">{totalCustomers}</p>
            </div>
            <User className="w-10 h-10 text-gold/30" />
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Ingresos Totales</p>
              <p className="text-2xl font-semibold text-gold mt-1">${totalRevenue.toFixed(2)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-gold/30" />
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Ticket Promedio</p>
              <p className="text-2xl font-semibold text-white mt-1">${avgOrderValue.toFixed(2)}</p>
            </div>
            <ShoppingBag className="w-10 h-10 text-gold/30" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por nombre, teléfono o email..."
          className="pl-10 bg-[#121212] border-white/10 text-white"
          data-testid="customer-search"
        />
      </div>

      {/* Customers Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-4 px-4 text-left text-white/60 text-sm font-medium">Cliente</th>
                <th className="py-4 px-4 text-left text-white/60 text-sm font-medium hidden md:table-cell">Contacto</th>
                <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Pedidos</th>
                <th className="py-4 px-4 text-right text-white/60 text-sm font-medium">Total Gastado</th>
                <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr 
                  key={customer.id} 
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() => openCustomerDetails(customer)}
                  data-testid={`customer-row-${customer.id}`}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{customer.name}</p>
                        <p className="text-white/40 text-xs">
                          Cliente desde {new Date(customer.created_at).toLocaleDateString('es-VE', { month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 hidden md:table-cell">
                    <div className="space-y-1">
                      <p className="text-white/60 text-sm flex items-center gap-2">
                        <Phone className="w-3 h-3" /> {customer.phone}
                      </p>
                      {customer.email && (
                        <p className="text-white/40 text-xs flex items-center gap-2">
                          <Mail className="w-3 h-3" /> {customer.email}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-white font-medium">{customer.total_orders || 0}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-gold font-semibold">${(customer.total_spent || 0).toFixed(2)}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <button 
                      onClick={(e) => { e.stopPropagation(); openCustomerDetails(customer); }}
                      className="p-2 text-white/40 hover:text-gold transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {customers.length === 0 && (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <p className="text-white/40">No hay clientes registrados</p>
              <p className="text-white/30 text-sm mt-2">Los clientes se crean automáticamente al realizar pedidos</p>
            </div>
          )}
        </div>
      )}

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Detalle del Cliente</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="p-4 bg-white/5 space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gold/10 flex items-center justify-center">
                    <User className="w-8 h-8 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-white">{selectedCustomer.name}</h3>
                    <p className="text-white/40 text-sm">
                      Cliente desde {new Date(selectedCustomer.created_at).toLocaleDateString('es-VE', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 text-white/60">
                    <Phone className="w-4 h-4" />
                    <span>{selectedCustomer.phone}</span>
                  </div>
                  {selectedCustomer.email && (
                    <div className="flex items-center gap-2 text-white/60">
                      <Mail className="w-4 h-4" />
                      <span>{selectedCustomer.email}</span>
                    </div>
                  )}
                  {selectedCustomer.address && (
                    <div className="col-span-2 flex items-center gap-2 text-white/60">
                      <MapPin className="w-4 h-4" />
                      <span>{selectedCustomer.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gold/10 border border-gold/20 text-center">
                  <p className="text-gold text-2xl font-semibold">{selectedCustomer.total_orders || 0}</p>
                  <p className="text-white/60 text-sm">Pedidos Realizados</p>
                </div>
                <div className="p-4 bg-gold/10 border border-gold/20 text-center">
                  <p className="text-gold text-2xl font-semibold">${(selectedCustomer.total_spent || 0).toFixed(2)}</p>
                  <p className="text-white/60 text-sm">Total Gastado</p>
                </div>
              </div>

              {/* Order History */}
              <div>
                <h4 className="text-gold text-sm font-medium uppercase tracking-wider mb-4">
                  Historial de Compras ({customerOrders.length})
                </h4>
                
                {customerOrders.length > 0 ? (
                  <div className="space-y-3">
                    {customerOrders.map((order) => (
                      <div 
                        key={order.id} 
                        className="p-4 bg-white/5 border border-white/10 hover:border-gold/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-gold font-medium">{order.order_number}</p>
                            <p className="text-white/40 text-xs flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(order.created_at).toLocaleDateString('es-VE', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-semibold">${order.total?.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-0.5 ${getStatusColor(order.order_status)}`}>
                              {getStatusLabel(order.order_status)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Order Items */}
                        <div className="border-t border-white/10 pt-3 space-y-2">
                          {order.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-white/60">
                                {item.quantity}x {item.product_name}
                                <span className="text-white/40 ml-2">({item.size}/{item.color})</span>
                              </span>
                              <span className="text-white/60">${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-white/5 border border-white/10">
                    <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No hay pedidos registrados</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <a
                href={`https://wa.me/${selectedCustomer.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${selectedCustomer.name}! Te escribimos desde D'Avocado Swimwear...`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#25D366] text-white text-center py-3 font-medium hover:bg-[#20BA5C] transition-colors"
              >
                Contactar por WhatsApp
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
